import { useEffect, useState } from 'react';
import { saudeCrossSystem } from '../api';
import { PageHeader, Loading, Alert, KPI } from '../components/common';

const STATUS_LABELS = {
  online:  { label: '✓ Online',  color: 'var(--success)', bg: 'rgba(52,211,153,0.10)' },
  offline: { label: '✗ Offline', color: 'var(--danger)',  bg: 'rgba(248,113,113,0.10)' },
  timeout: { label: '⏱ Timeout', color: 'var(--warning)', bg: 'rgba(251,191,36,0.10)' },
  erro:    { label: '⚠ Erro',    color: 'var(--danger)',  bg: 'rgba(248,113,113,0.10)' },
};

export default function SaudeCrossSystem() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const carregar = (refresh = false) => {
    setLoading(true);
    setErro(null);
    saudeCrossSystem(refresh)
      .then(setDados)
      .catch(err => setErro(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    carregar();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => carregar(false), 30_000);  // refresh 30s
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (loading && !dados) return <Loading texto="Verificando saúde dos sistemas…" />;

  const agg = dados?.agregado || {};

  return (
    <>
      <PageHeader
        title="Saúde do Ecossistema"
        sub="Healthcheck cross-system dos 6 sistemas Átrio (refresh 30s)"
        actions={
          <>
            <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
                style={{ width: 'auto' }}
              />
              Auto-refresh 30s
            </label>
            <button onClick={() => carregar(true)}>↻ Verificar agora</button>
          </>
        }
      />

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {dados && (
        <>
          <div className="kpi-grid">
            <KPI
              label="Saúde geral"
              value={
                agg.saude_geral === 'tudo_ok' ? 'Tudo OK ✓' :
                agg.saude_geral === 'tudo_offline' ? 'Crítico ✗' :
                'Parcial ⚠'
              }
              variant={
                agg.saude_geral === 'tudo_ok' ? 'success' :
                agg.saude_geral === 'tudo_offline' ? 'danger' : 'warning'
              }
              sub={dados.cacheado ? `cache ${Math.round((dados.idade_ms || 0) / 1000)}s` : 'tempo real'}
            />
            <KPI label="Online" value={agg.online ?? 0} variant="success" />
            <KPI label="Offline" value={agg.offline ?? 0} variant={agg.offline > 0 ? 'danger' : ''} />
            <KPI label="Timeout" value={agg.timeout ?? 0} variant={agg.timeout > 0 ? 'warning' : ''} />
            <KPI label="Erro HTTP" value={agg.erro ?? 0} variant={agg.erro > 0 ? 'danger' : ''} />
          </div>

          <div className="card" style={{ padding: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>Sistema</th>
                  <th>Status</th>
                  <th>HTTP</th>
                  <th className="num">Latência</th>
                  <th>URL</th>
                  <th>Detalhe</th>
                  <th>Verificado em</th>
                </tr>
              </thead>
              <tbody>
                {dados.sistemas.map(s => {
                  const meta = STATUS_LABELS[s.status] || { label: s.status, color: 'var(--text)', bg: 'transparent' };
                  return (
                    <tr key={s.id}>
                      <td className="bold">
                        {s.nome}
                        {s.self && <span className="small muted" style={{ marginLeft: 8 }}>(self)</span>}
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: 99,
                          background: meta.bg,
                          color: meta.color,
                          fontWeight: 600,
                          fontSize: 11,
                          fontFamily: 'var(--font-data)',
                        }}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="mono small">{s.http || '—'}</td>
                      <td className="num small mono">
                        {s.latencia_ms != null ? `${s.latencia_ms}ms` : '—'}
                      </td>
                      <td className="small muted" style={{ fontFamily: 'var(--font-data)' }}>{s.url}</td>
                      <td className="small">
                        {s.erro ? <span style={{ color: 'var(--danger)' }}>{s.erro}</span> :
                          s.detalhe ? (
                            <details>
                              <summary className="muted">ver JSON</summary>
                              <pre style={{
                                fontSize: 10,
                                fontFamily: 'var(--font-data)',
                                background: 'var(--void-2)',
                                padding: 6,
                                borderRadius: 4,
                                marginTop: 4,
                                maxWidth: 320,
                                overflow: 'auto',
                              }}>
                                {JSON.stringify(s.detalhe, null, 2)}
                              </pre>
                            </details>
                          ) : '—'}
                      </td>
                      <td className="small mono muted">
                        {s.checado_em ? new Date(s.checado_em).toLocaleTimeString('pt-BR') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="small muted" style={{ marginTop: 16 }}>
            Verificado em {agg.verificado_em ? new Date(agg.verificado_em).toLocaleString('pt-BR') : '—'}
            {' · '}
            Timeout por sistema: 3s · Cache: 30s · Forçar refresh ignora cache
          </p>
        </>
      )}
    </>
  );
}
